import { findPath, isWalkable } from './Navigation.js'
import { getMachinePort, MACHINE_TYPES, TUNING } from './definitions.js'

export default class BotSystem
{
    constructor(_state)
    {
        this.state = _state
        this.bots = []
        this.nextBotId = 1
    }

    restore(_save)
    {
        if(!_save)
            return
        this.nextBotId = _save.nextBotId ?? this.nextBotId
        if(Array.isArray(_save.bots))
        {
            this.bots = _save.bots.map((_bot) => ({
                state: 'idle', carrying: 0, path: [], targetId: null, reservedSource: 0, timer: 0, cooldown: 0, moving: false,
                ..._bot,
                path: Array.isArray(_bot.path) ? _bot.path : []
            }))
            for(const bot of this.bots)
            {
                const source = this.state.machines.find((_machine) => _machine.id === bot.sourceId)
                const target = this.state.machines.find((_machine) => _machine.id === bot.targetId)
                if(bot.state === 'sourceTravel' && source)
                    this.setDestination(bot, getMachinePort(source, 'output'))
                else if(bot.state === 'targetTravel' && target)
                    this.setDestination(bot, getMachinePort(target, 'fuel'))
                else if(bot.state === 'loading' && source)
                {
                    const port = getMachinePort(source, 'output')
                    Object.assign(bot, { x: port.x, y: port.y, path: [], destination: null })
                }
                else if(bot.state === 'unloading' && target)
                {
                    const port = getMachinePort(target, 'fuel')
                    Object.assign(bot, { x: port.x, y: port.y, path: [], destination: null })
                }
                else if(['sourceTravel', 'targetTravel', 'loading', 'unloading'].includes(bot.state))
                {
                    bot.state = 'idle'
                    bot.path = []
                    bot.destination = null
                    bot.targetId = null
                    bot.reservedSource = 0
                }
            }
        }
    }

    toJSON()
    {
        return { nextBotId: this.nextBotId, bots: this.bots }
    }

    deploy()
    {
        const bot = {
            id: `coalBot${this.nextBotId++}`,
            x: 6.1,
            y: -13,
            state: 'idle',
            carrying: 0,
            path: [],
            destination: null,
            targetId: null,
            sourceId: 'coalDrill',
            reservedSource: 0,
            timer: 0,
            cooldown: 0.3,
            moving: false
        }
        this.bots.push(bot)
        this.state.trigger('botDeployed', [bot])
        return bot
    }

    update(_delta)
    {
        for(const bot of this.bots)
        {
            bot.cooldown = Math.max(0, bot.cooldown - _delta)
            bot.moving = false
            if(bot.state === 'idle')
                this.updateIdle(bot)
            else if(bot.state === 'sourceTravel')
                this.updateSourceTravel(bot, _delta)
            else if(bot.state === 'loading')
                this.updateLoading(bot, _delta)
            else if(bot.state === 'targetTravel')
                this.updateTargetTravel(bot, _delta)
            else if(bot.state === 'unloading')
                this.updateUnloading(bot, _delta)
        }
    }

    updateIdle(_bot)
    {
        if(_bot.cooldown > 0)
            return
        if(_bot.carrying > 0)
        {
            this.chooseTarget(_bot, true)
            return
        }
        const source = this.state.machines.find((_machine) => _machine.id === 'coalDrill')
        if(!source)
            return
        const reserved = this.bots.reduce((_total, _other) => _total + (_other.id === _bot.id ? 0 : _other.reservedSource), 0)
        const available = Math.max(0, source.output - reserved)
        if(available <= 0)
        {
            _bot.cooldown = 0.35
            return
        }
        _bot.reservedSource = Math.min(TUNING.botCapacity, available)
        _bot.sourceId = source.id
        const port = getMachinePort(source, 'output')
        if(this.setDestination(_bot, port))
            _bot.state = 'sourceTravel'
        else
        {
            _bot.reservedSource = 0
            _bot.cooldown = 0.5
        }
    }

    updateSourceTravel(_bot, _delta)
    {
        if(!this.followPath(_bot, _delta))
            return
        _bot.state = 'loading'
        _bot.timer = 0.35
    }

    updateLoading(_bot, _delta)
    {
        _bot.timer -= _delta
        if(_bot.timer > 0)
            return
        const source = this.state.machines.find((_machine) => _machine.id === _bot.sourceId)
        const amount = source ? Math.min(TUNING.botCapacity, _bot.reservedSource || TUNING.botCapacity, source.output) : 0
        if(source && amount > 0)
        {
            source.output -= amount
            _bot.carrying = amount
            this.state.trigger('machineChanged', [source])
            this.state.trigger('botLoaded', [{ bot: _bot, machine: source, resource: 'coal', amount, automated: true }])
        }
        _bot.reservedSource = 0
        if(_bot.carrying > 0)
            this.chooseTarget(_bot, true)
        else
        {
            _bot.state = 'idle'
            _bot.cooldown = 0.5
        }
    }

    updateTargetTravel(_bot, _delta)
    {
        if(!_bot.targetId || !this.getTargetCapacity(_bot, _bot.targetId))
        {
            this.chooseTarget(_bot, true)
            return
        }
        if(this.followPath(_bot, _delta))
        {
            _bot.state = 'unloading'
            _bot.timer = 0.12
        }
    }

    updateUnloading(_bot, _delta)
    {
        _bot.timer -= _delta
        if(_bot.timer > 0)
            return
        const machine = this.state.machines.find((_machine) => _machine.id === _bot.targetId)
        if(!machine || !this.getTargetCapacity(_bot, _bot.targetId))
        {
            _bot.targetId = null
            _bot.state = 'idle'
            _bot.cooldown = 0.2
            return
        }
        machine.inputs.coal = (machine.inputs.coal ?? 0) + 1
        machine.receivedInputs.add('coal')
        _bot.carrying--
        _bot.timer = TUNING.transferInterval
        this.state.trigger('machineChanged', [machine])
        this.state.trigger('botUnloaded', [{ bot: _bot, machine, resource: 'coal', amount: 1, sourceId: _bot.sourceId, automated: true }])
        if(_bot.carrying === 0)
        {
            _bot.targetId = null
            _bot.state = 'idle'
            _bot.cooldown = 0.25
        }
    }

    getIncoming(_machineId, _exceptBot = null)
    {
        return this.bots.reduce((_total, _bot) => _total + (_bot.id !== _exceptBot?.id && _bot.targetId === _machineId ? _bot.carrying : 0), 0)
    }

    getTargetCapacity(_bot, _machineId)
    {
        const machine = this.state.machines.find((_machine) => _machine.id === _machineId)
        if(!machine)
            return 0
        const stored = (machine.inputs.coal ?? 0) + (machine.inputs.timber ?? 0)
        return Math.max(0, TUNING.machineSlotCapacity - stored - this.getIncoming(_machineId, _bot))
    }

    getEstimatedFuel(_machine, _exceptBot = null)
    {
        const incoming = this.getIncoming(_machine.id, _exceptBot)
        return _machine.fuelRemaining + (_machine.inputs.coal ?? 0) * TUNING.fuelSeconds.coal + (_machine.inputs.timber ?? 0) * TUNING.fuelSeconds.timber + incoming * TUNING.fuelSeconds.coal
    }

    chooseTarget(_bot, _force)
    {
        const candidates = this.state.machines
            .filter((_machine) => MACHINE_TYPES[_machine.type].fuel && this.getTargetCapacity(_bot, _machine.id) > 0)
            .map((_machine) => ({ machine: _machine, estimated: this.getEstimatedFuel(_machine, _bot) }))
            .sort((_a, _b) => _a.estimated - _b.estimated)
        const best = candidates[0]?.machine
        if(!best)
        {
            _bot.targetId = null
            _bot.state = 'idle'
            _bot.cooldown = 0.45
            return false
        }
        if(!_force && _bot.targetId === best.id)
            return true
        _bot.targetId = best.id
        const port = getMachinePort(best, 'fuel')
        if(this.setDestination(_bot, port))
        {
            _bot.state = 'targetTravel'
            return true
        }
        _bot.targetId = null
        _bot.state = 'idle'
        _bot.cooldown = 0.5
        return false
    }

    setDestination(_bot, _port)
    {
        _bot.destination = { x: _port.x, y: _port.y }
        _bot.path = findPath(this.state, _bot.x, _bot.y, _port.x, _port.y)
        return _bot.path.length > 0
    }

    followPath(_bot, _delta)
    {
        if(_bot.path.length === 0 && _bot.destination)
            _bot.path = findPath(this.state, _bot.x, _bot.y, _bot.destination.x, _bot.destination.y)
        if(_bot.path.length === 0)
            return false
        const target = _bot.path[0]
        const offsetX = target.x - _bot.x
        const offsetY = target.y - _bot.y
        const distance = Math.hypot(offsetX, offsetY)
        const step = TUNING.botSpeed * _delta
        if(distance <= step + 0.0001)
        {
            _bot.x = target.x
            _bot.y = target.y
            _bot.path.shift()
            return _bot.path.length === 0
        }
        const nextX = _bot.x + offsetX / distance * step
        const nextY = _bot.y + offsetY / distance * step
        if(isWalkable(this.state, nextX, nextY))
        {
            _bot.x = nextX
            _bot.y = nextY
            _bot.moving = true
        }
        else if(_bot.destination)
            _bot.path = findPath(this.state, _bot.x, _bot.y, _bot.destination.x, _bot.destination.y)
        return false
    }
}
